import { useRef } from 'react';
import { clampCircleCenter, clientToImagePx } from './circleGeometry';

/**
 * SVG overlay-круг для PhotoPicker (Экран 2.2.3, ADR-0008).
 *
 * Координаты круга — в **image-pixel space** (cx/cy/r ∈ [0, imageWidth/Height]).
 * SVG `viewBox` совпадает с image-размером, поэтому браузер автоматически
 * масштабирует SVG под отображаемые габариты, а наш код переводит client-px
 * → image-px через `getBoundingClientRect()` ratio (`clientToImagePx`).
 *
 * Pointer events (touch + mouse одной логикой) — `setPointerCapture` обёрнут
 * try/catch для совместимости с jsdom (тестовая среда без поддержки).
 *
 * **Интерактивна вся площадь оверлея** (PRD 2.2.3 «tap фиксирует центр круга;
 * drag двигает»): `pointerDown` в любой точке ставит центр туда + стартует drag.
 * Это чинит pre-W2 проблему — раньше интерактивен был только мелкий `<circle>`,
 * на телефоне трудно попасть пальцем, а вне круга палец уходил в скролл.
 * `touchAction:'none'` на всём оверлее — драг не конкурирует со скроллом.
 *
 * Clamp (ADR-0008 §3): круг целиком внутри изображения, край не выходит.
 * Это упрощает `extractLab` — partial overlap логика не активируется.
 */
export interface CirclePickerOverlayProps {
  imageWidth: number;
  imageHeight: number;
  cx: number;
  cy: number;
  radius: number;
  onMove: (cx: number, cy: number) => void;
}

export function CirclePickerOverlay({
  imageWidth,
  imageHeight,
  cx,
  cy,
  radius,
  onMove,
}: CirclePickerOverlayProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const isDraggingRef = useRef(false);

  // Перевод pointer → image-px + clamp + onMove (учитывает letterbox в
  // clientToImagePx). Используется и на tap (pointerDown), и на drag (pointerMove).
  function moveToPointer(clientX: number, clientY: number) {
    const svg = svgRef.current;
    if (!svg) return;
    const { x, y } = clientToImagePx(
      clientX,
      clientY,
      svg.getBoundingClientRect(),
      imageWidth,
      imageHeight,
    );
    const clamped = clampCircleCenter(x, y, radius, imageWidth, imageHeight);
    onMove(clamped.cx, clamped.cy);
  }

  function onPointerDown(e: React.PointerEvent) {
    isDraggingRef.current = true;
    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {
      /* graceful no-op for jsdom / older browsers */
    }
    // Tap фиксирует центр круга сразу в точке нажатия (PRD 2.2.3).
    moveToPointer(e.clientX, e.clientY);
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!isDraggingRef.current) return;
    moveToPointer(e.clientX, e.clientY);
  }

  function onPointerUp(e: React.PointerEvent) {
    isDraggingRef.current = false;
    try {
      (e.currentTarget as Element).releasePointerCapture(e.pointerId);
    } catch {
      /* graceful no-op */
    }
  }

  return (
    <svg
      ref={svgRef}
      role="img"
      aria-label="Передвинуть круг подбора"
      viewBox={`0 0 ${imageWidth} ${imageHeight}`}
      preserveAspectRatio="xMidYMid meet"
      className="absolute inset-0 w-full h-full"
      style={{ pointerEvents: 'auto', cursor: 'grab', touchAction: 'none' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      <circle
        data-testid="picker-circle"
        cx={cx}
        cy={cy}
        r={radius}
        fill="rgba(245, 196, 0, 0.18)"
        stroke="#f5c400"
        strokeWidth={Math.max(2, imageWidth * 0.005)}
        style={{ pointerEvents: 'none' }}
      />
    </svg>
  );
}
